import { useState, useRef, useCallback } from 'react';
import AnnotationCanvas from './AnnotationCanvas';
import type { Annotation } from '../../api/client';
import type { AnnotationTool } from './AnnotationToolbar';
import { ZoomIn, ZoomOut, Loader2, AlertTriangle } from 'lucide-react';

interface ImageViewerProps {
  url: string;
  alt: string;
  annotations: Annotation[];
  onAnnotationCreate: (annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
  }) => void;
  onAnnotationDelete: (annotationId: string) => void;
  activeTool: AnnotationTool;
  activeColor: string;
  rectangleFill?: boolean;
  readOnly?: boolean;
}

export default function ImageViewer({
  url,
  alt,
  annotations,
  onAnnotationCreate,
  onAnnotationDelete,
  activeTool,
  activeColor,
  rectangleFill = false,
  readOnly = false,
}: ImageViewerProps) {
  const [scale, setScale] = useState(1.0);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const containerWidth = containerRef.current?.clientWidth || 800;
    const naturalWidth = img.naturalWidth;
    const naturalHeight = img.naturalHeight;

    // Fit to container
    const fitScale = Math.min((containerWidth - 32) / naturalWidth, 1.0);
    setImageSize({ width: naturalWidth, height: naturalHeight });
    setScale(fitScale);
    setLoading(false);
  }, []);

  const scaledWidth = imageSize.width * scale;
  const scaledHeight = imageSize.height * scale;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertTriangle className="w-8 h-8 mb-2" />
        <p className="text-sm">Failed to load image</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Zoom controls */}
      <div className="flex items-center justify-end bg-gray-50 border-b border-gray-200 px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setScale(Math.max(0.25, scale - 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale(Math.min(3.0, scale + 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Image Content */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 p-4">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading image...</span>
          </div>
        )}
        <div className="flex justify-center">
          <div className="relative" style={{ width: scaledWidth || 'auto', height: scaledHeight || 'auto' }}>
            <img
              src={url}
              alt={alt}
              onLoad={handleImageLoad}
              onError={() => { setError(true); setLoading(false); }}
              className="block"
              style={{ width: scaledWidth || 'auto', height: scaledHeight || 'auto' }}
              draggable={false}
            />

            {/* Annotation overlay */}
            {scaledWidth > 0 && scaledHeight > 0 && !loading && (
              <AnnotationCanvas
                annotations={annotations}
                onAnnotationCreate={onAnnotationCreate}
                onAnnotationDelete={onAnnotationDelete}
                activeTool={activeTool}
                activeColor={activeColor}
                pageNumber={null}
                width={scaledWidth}
                height={scaledHeight}
                readOnly={readOnly}
                rectangleFill={rectangleFill}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
